import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { addSession, getSessionById, saveAiChatPolicy } from '../../../service/sessionService'
import { updateTestCodeInSession } from '../../../service/testService'
import { getExperimentSupport, publishExperiment, refreshSignIn } from '../../../service/experimentService'
import { showToast } from '../../commonUnit/Toast'
import {
  aiChatSupport,
  buildAiChatUpdate,
  defaultAiChatFor,
  describeSaveFailure,
  saveSessionFields,
  testCodeForTestsSetting,
} from '../sessionSettings'
import LiveStudentPreview from '../studentView/live/LiveStudentPreview'
import {
  assignmentLabel,
  buildPublishPayload,
  conditionAiChat,
  conditionPolicy,
  newExperiment,
  validateExperiment,
} from '../experiment/experimentConfig'

import { getStepsForType, getTypeMeta, supportsTestsToggle } from './sessionTypeCatalog'
import DetailsStep from './steps/DetailsStep'
import TypeStep from './steps/TypeStep'
import TestCasesStep from './steps/TestCasesStep'
import CodeStubStep from './steps/CodeStubStep'
import GuiExerciseStep from './steps/GuiExerciseStep'
import PopQuizStep from './steps/PopQuizStep'
import AiChatStep from './steps/AiChatStep'
import ExperimentStep from './steps/ExperimentStep'

import '../../../css/createwizard.scss'

// Same placeholder-CRN reasoning as AddSessionDialog.jsx:28-35 - session.crn is NOT NULL
// in Postgres but nothing reads it as a real course identifier anymore.
const generatePlaceholderCrn = () => Date.now().toString(36).slice(-10)


const HEADER_BY_KIND = {
  code: 'Create Coding Problem',
  sql: 'Create Coding Problem',
  gui: 'Create Web Problem',
  quiz: 'Create Pop Quiz',
}

// The part of the student's screen each step configures, highlighted in the live preview
// (region ids from studentView/live/previewRegions.js; 'all' = the whole view).
const PREVIEW_FOCUS = {
  details: ['task'],
  type: ['all'],
  tests: ['tests', 'run'],
  stubs: ['editor'],
  gui: ['task', 'editor', 'tests'],
  quiz: ['all'],
  ai: ['chat'],
  experiment: ['chat'],
}

// Width of the steps column, remembered per browser. Every storage access is guarded:
// storage can be unavailable (private windows, blocked site data).
const STEPS_WIDTH_KEY = 'vizpi.wizard.stepsWidth'
const STEPS_WIDTH_DEFAULT = 480
const STEPS_WIDTH_MIN = 380
const PREVIEW_WIDTH_MIN = 520
// Horizontal space the split spends on its own padding and the divider.
const SPLIT_CHROME = 24 + 16 + 12
// A width remembered on a bigger screen must not push the preview (and the Continue button
// at the right of each step) off this one; the CSS also lets the column shrink.
const clampStepsWidth = (width, available) =>
  Math.min(Math.max(STEPS_WIDTH_MIN, available - SPLIT_CHROME - PREVIEW_WIDTH_MIN), Math.max(STEPS_WIDTH_MIN, width))
const readStepsWidth = () => {
  let stored = STEPS_WIDTH_DEFAULT
  try {
    const value = Number(window.localStorage.getItem(STEPS_WIDTH_KEY))
    if (value >= STEPS_WIDTH_MIN) stored = value
  } catch (error) {
    // Fall back to the default.
  }
  return clampStepsWidth(stored, window.innerWidth)
}
const writeStepsWidth = (width) => {
  try {
    window.localStorage.setItem(STEPS_WIDTH_KEY, String(Math.round(width)))
  } catch (error) {
    // Not remembering it is fine.
  }
}

// Step-by-step replacement for the single AddSessionDialog form. The dialog is untouched
// and still reachable from the session list ("Quick add"); this is the guided path.
//
// The session row is created when the instructor leaves the Language step, not at the end.
// That is forced by the code: every panel this wizard reuses (TestList, StarterCode,
// GuiExerciseEditor, PopQuizEditor) reads SessionContext and persists against session.id,
// and the create endpoint dereferences session.type unguarded
// (vizpi_backend/services/sessionService.js:37), so type is the one field that cannot be
// deferred. Creating early also means a half-finished exercise survives a closed tab.
const CreateQuestionWizard = () => {
  const navigate = useNavigate()
  const { currentUser } = useContext(AuthContext)

  const [subject, setSubject] = useState('')
  const [task, setTask] = useState('')
  const [type, setType] = useState('')
  // Code types only: whether the exercise has test cases at all, answered on the Testcases
  // step and stored as session.enable_tests. Off leaves that step with nothing to set up.
  const [testsOn, setTestsOn] = useState(true)
  // Settled in the AI Chat step and written on Finish. Starts from the type's existing
  // default (addSession's enable_chat rule) so an untouched switch changes nothing.
  const [aiChatOn, setAiChatOn] = useState(false)
  const [aiPolicy, setAiPolicy] = useState('')

  const handleTypeChange = (nextType) => {
    setType(nextType)
    setAiChatOn(defaultAiChatFor(nextType))
  }

  const [createdSession, setCreatedSession] = useState(null)

  // ---- experiment mode ----
  // The header switch turns this draft into an experiment: an extra last step, and Publish
  // instead of Finish. Offered only when the backend can publish one (a frontend deployed
  // ahead of the backend shows no switch). The settings live here until Publish - they are
  // kept while the switch is off, so turning it back on restores them.
  const [experimentSupported, setExperimentSupported] = useState(false)
  const [experimentOn, setExperimentOn] = useState(false)
  const [experiment, setExperiment] = useState(null)
  const [previewConditionIndex, setPreviewConditionIndex] = useState(0)
  const [leaveOpen, setLeaveOpen] = useState(false)
  // An AI draft of the concept list is in flight; Publish waits for it so it never sends the
  // list the draft is about to replace.
  const [conceptDrafting, setConceptDrafting] = useState(false)
  useEffect(() => {
    let cancelled = false
    getExperimentSupport().then((supported) => {
      if (!cancelled) setExperimentSupported(supported)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Once the row exists it is the source of truth for the problem name and text: the
  // web-exercise and Pop Quiz editors save `task` themselves (assignment / quiz title).
  // Pull those saves back into the form so the preview shows them and re-opening Problem
  // Details never writes the old text back over them. Skipped while Problem Details is open
  // so the instructor's own typing is never replaced.
  const detailsOpenRef = useRef(true)
  useEffect(() => {
    if (detailsOpenRef.current) return
    if (typeof createdSession?.task === 'string') setTask(createdSession.task)
  }, [createdSession?.task])
  useEffect(() => {
    if (detailsOpenRef.current) return
    if (typeof createdSession?.subject === 'string') setSubject(createdSession.subject)
  }, [createdSession?.subject])
  const [link, setLink] = useState('')

  const [activeStep, setActiveStep] = useState(0)
  const [isBusy, setIsBusy] = useState(false)
  const [formError, setFormError] = useState('')
  // Finish warns once when the last step's work looks unsaved, then lets the second click
  // through - the instructor may genuinely want to leave it half-authored.
  const [finishWarned, setFinishWarned] = useState(false)
  // Published by TestList on the Testcases step: { run, testCount, loading }. The wizard
  // calls run() from Next instead of showing TestList's own "Create Unit Tests" button.
  const createUnitTestsRef = useRef(null)
  // Next pauses once on the Testcases step when the compile flags cases as disagreeing
  // with the AI reference solution, so the instructor actually sees the suggestions.
  const [testsWarned, setTestsWarned] = useState(false)
  // What the harness was last compiled from (cases + task + type). Revisiting Testcases and
  // pressing Next again must not silently regenerate it: that rewrites Variable Setup /
  // Unit Test, including edits made on Code Stubs, and repeats the slow AI calls.
  const lastCompiledRef = useRef(null)
  const [recompileWarned, setRecompileWarned] = useState(false)
  // Bumped on every refetch so steps that embed a blur-saving editor can remount and
  // re-seed from the row we just read back.
  const [refreshToken, setRefreshToken] = useState(0)

  // ---- live preview ----
  // Unsaved edits the preview shows as they are typed: the test list on the Testcases step
  // (TestList's local state) and the student code template on the Code Stubs step
  // (StarterCode's). Each only applies while its own step is open - once the step is left
  // the row is read back and is the source of truth again.
  const [draftTestList, setDraftTestList] = useState(null)
  const [draftTemplate, setDraftTemplate] = useState(null)
  // Bumping this resets the preview's student side (their code, output, chat).
  const [previewResetToken, setPreviewResetToken] = useState(0)
  // The AI Chat Policy the server has for this session, as far as the wizard knows. The
  // preview's chat goes through the same server route as students, which applies the
  // stored policy - so the draft is saved before each preview message.
  const savedPolicyRef = useRef(null)

  // ---- resizable split ----
  const layoutRef = useRef(null)
  const [stepsWidth, setStepsWidth] = useState(readStepsWidth)
  const dragCleanupRef = useRef(null)
  useEffect(() => () => dragCleanupRef.current?.(), [])

  const steps = useMemo(() => getStepsForType(type, { experiment: experimentOn }), [type, experimentOn])
  const typeMeta = getTypeMeta(type)
  const current = steps[activeStep]
  const isLastStep = activeStep === steps.length - 1
  const finishLabel = experimentOn ? 'Publish' : 'Finish'
  // Read by the subject/task sync effects above (effects run after this render).
  detailsOpenRef.current = current?.id === 'details'

  // Messages sit next to Continue, at the bottom of a step that can be long; bring them
  // into view when they appear so a pause or a validation error is never missed.
  const errorRef = useRef(null)
  useEffect(() => {
    if (formError) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [formError])

  // After Continue / Back / reopening a step, the button that had focus is gone (the step
  // it lived in collapsed). Move focus to the newly opened step so keyboard and screen
  // reader users land at its start instead of the top of the page.
  const activeBodyRef = useRef(null)
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    const focused = document.activeElement
    if (!focused || focused === document.body) activeBodyRef.current?.focus()
  }, [activeStep])

  // The wizard deliberately shadows the app-level SessionContext for its subtree. The
  // outer SessionProvider keys its fetch/websocket/10s poll on currentUser.session_id
  // (SessionContext.js:14-58), which still points at whatever session was open last - so
  // without this, the poll would overwrite our freshly created draft every 10 seconds.
  // Providing the same value shape means every embedded panel works unmodified.
  const sessionContextValue = useMemo(
    () => ({
      session: createdSession,
      setSession: setCreatedSession,
      sessionLoading: false,
      link,
      setLink,
    }),
    [createdSession, link]
  )

  const refreshSession = async () => {
    if (!createdSession?.id) return null
    try {
      const fresh = await getSessionById(createdSession.id)
      if (fresh) {
        setCreatedSession(fresh)
        setRefreshToken((n) => n + 1)
        return fresh
      }
    } catch (error) {
      console.error('Could not refresh the session between steps:', error)
    }
    return null
  }

  // A session created for a code type has test_code = null until TestList's "Create Unit
  // Tests" writes it. StarterCode seeds its three editors from session.test_code in an
  // effect that early-returns when the column is null (StarterCode.jsx:26), leaving the
  // editors on their '#StarterCode' / '#Unit Test' placeholder defaults - which its own
  // onBlur handler then saves over the session as if the instructor had typed them.
  // Writing an empty shape before the Code Stubs step mounts means the editors seed from
  // '' instead, so a blur with nothing typed is a no-op rather than data.
  //
  // It MUST be told the freshly fetched row. TestList writes test_code straight to the
  // server and never calls setSession, so `createdSession` in React state still has
  // test_code === null right after a successful "Create Unit Tests" - deciding from it
  // would blank the harness the instructor just generated.
  // Returns the row the next step should render against - patched in place when it wrote,
  // because refreshSession has already run by the time this is called and re-fetching
  // again just to see our own write would be a second round trip.
  // Returns null when the write failed (updateTestCodeInSession reports failure by
  // returning false, never by throwing).
  const ensureTestCode = async (freshSession) => {
    if (!freshSession?.id || freshSession.test_code) return freshSession
    const empty = { starter: '', unit_test: '', template: '' }
    if (!(await updateTestCodeInSession(freshSession.id, empty))) return null
    const patched = { ...freshSession, test_code: empty }
    setCreatedSession(patched)
    setRefreshToken((n) => n + 1)
    return patched
  }

  // Reads the row we just fetched and says what, if anything, still looks unauthored.
  // Returns '' when the exercise is in a state a student could actually be given.
  const describeIncompleteness = (row, actionLabel = finishLabel) => {
    if (!row) return ''
    const kind = typeMeta?.kind
    if (kind === 'quiz') {
      return row.quiz?.questions?.length
        ? ''
        : `This quiz has no saved questions yet. Use Save in the editor above, or click ${actionLabel} again to leave it empty.`
    }
    if (kind === 'gui') {
      return row.test_code?.unit_test
        ? ''
        : `The exercise has not been saved yet. Use Save in the editor above, or click ${actionLabel} again to leave it unsaved.`
    }
    // A code exercise with test cases off has no harness by design.
    if (kind === 'code' && !testsOn) return ''
    // code and sql both grade off test_code.unit_test.
    return row.test_code?.unit_test
      ? ''
      : `No test harness has been saved for this exercise, so every test will report as not run. Click ${actionLabel} again to leave it this way.`
  }

  // Writes session.enable_tests when it differs from the switch. A backend without the
  // column reads as "tests on" (sessionSettings.testsEnabled), so "on" never needs a write
  // there - only "off" does, and that one must be verified, or students would silently
  // keep seeing a Test Cases section the instructor turned off.
  const syncTestsSetting = async (row) => {
    if (!supportsTestsToggle(type)) return { ok: true, session: row }
    const needsWrite = testsOn ? row.enable_tests === false : row.enable_tests !== false
    if (!needsWrite) return { ok: true, session: row }
    return saveSessionFields(row.id, { enable_tests: testsOn })
  }

  // Code types: make sure test_code exists before any step that embeds StarterCode, so its
  // editors seed from '' rather than their '#StarterCode' / '#Unit Test' placeholders - the
  // Testcases step (which used to be the only place this happened) is skipped entirely when
  // tests are off. For C++, also keep the template's main() in step with the toggle (see
  // sessionSettings.testCodeForTestsSetting).
  const seedTestCode = async (row) => {
    if (typeMeta?.kind !== 'code') return row
    const next = testCodeForTestsSetting(row, testsOn)
    if (!next) return row
    if (!(await updateTestCodeInSession(row.id, next))) {
      // Thrown into the 'type' branch's catch, which shows it; the row already exists, so
      // the next click retries only this write.
      throw new Error('Could not save the starter code. Check your connection and press Continue again.')
    }
    return { ...row, test_code: next }
  }

  const createSession = async () => {
    // Pop Quiz reserves an empty question list so PopQuizEditor has somewhere to save to,
    // matching AddSessionDialog.jsx:86-89.
    const quiz = type === 'Pop Quiz' ? { questions: [] } : undefined
    const created = await addSession(
      generatePlaceholderCrn(),
      task,
      subject,
      type,
      currentUser.id,
      currentUser.uid,
      quiz
    )
    if (!created) throw new Error('VizPI could not create the session.')
    return created
  }

  // The AI Chat step's settings, written on Finish. Returns false (with the error shown) when
  // a write failed. Not used in experiment mode, which has no AI Chat step: each condition
  // carries its own chat settings to Publish.
  const saveAiSettings = async (row) => {
    // One PUT for the chat switch, including the Python -> Vizmental type switch that "AI
    // chat on" means for the Python family.
    const fields = buildAiChatUpdate(row.type, aiChatOn)
    if (Object.keys(fields).length) {
      const result = await saveSessionFields(createdSession.id, fields)
      if (!result.ok) {
        setFormError(describeSaveFailure(result))
        return false
      }
    }
    // The policy goes through its own instructor-only endpoint; it is never part of the
    // session object. The field is only shown while chat is on, so text typed and then
    // hidden by switching chat off is kept in the form but not saved. A new session has no
    // stored policy, so nothing needs clearing when it is empty.
    const policy = aiChatOn ? aiPolicy.trim() : ''
    if (policy || savedPolicyRef.current) {
      const saved = await saveAiChatPolicy(createdSession.id, policy || null)
      if (saved.ok) savedPolicyRef.current = saved.policy ?? (policy || null)
      if (!saved.ok) {
        setFormError(
          saved.status === 401 || saved.status === 403
            ? `Only a signed-in instructor can save the AI Chat Policy. Sign in again and press ${finishLabel}.`
            : saved.status === 404 || saved.status === 500
            ? 'The server could not store the AI Chat Policy - the backend may be missing the ai_chat_policy column or the latest deploy.'
            : `Could not save the AI Chat Policy. Check your connection and press ${finishLabel} again.`
        )
        return false
      }
    }
    return true
  }

  const handleExperimentToggle = (on) => {
    if (isBusy) return
    setFormError('')
    // The AI Chat step gives way to per-condition settings: start every condition from what
    // that step holds (once a language is picked, its switch is meaningful), so nothing set
    // there is lost. The AI Chat step's own values are kept for when the switch goes off.
    if (on && !experiment) {
      setExperiment(newExperiment(subject.trim(), type ? { aiChat: aiChatOn, aiPolicy } : {}))
      setPreviewConditionIndex(0)
    }
    setExperimentOn(on)
    // The Experiment step takes the AI Chat step's place at the end (or is added after the
    // last step for types without AI chat), so the open step only needs clamping when the
    // Experiment step goes away.
    if (!on) setActiveStep((step) => Math.min(step, getStepsForType(type).length - 1))
  }

  // Experiment settings exist only in this form until Publish, so leaving loses them.
  const handleLeave = () => {
    if (experimentOn) setLeaveOpen(true)
    else navigate('/session')
  }

  // A name typed after the switch was turned on: start the experiment's name from it.
  useEffect(() => {
    if (current?.id !== 'experiment' || !experiment || experiment.name.trim() || !subject.trim()) return
    setExperiment((prev) => ({ ...prev, name: subject.trim() }))
  }, [current?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = async () => {
    if (isBusy || conceptDrafting) return
    setFormError('')
    // Every branch below can await, and setActiveStep(s => s + 1) would compound if two
    // of them ever overlapped. Move to an absolute target captured up front instead.
    const from = activeStep

    if (current.id === 'details') {
      if (!subject.trim() || !task.trim()) {
        setFormError('Add a problem name and a problem description before continuing.')
        return
      }
      // Coming back to fix a typo after the row exists has to write through - nothing else
      // in this flow persists subject/task, and PUT /sessions/:id takes both.
      if (createdSession?.id) {
        setIsBusy(true)
        try {
          // saveSessionFields, not updateSessionFields directly: the latter never throws
          // (it returns false), so a try/catch around it could not see a failed save.
          const result = await saveSessionFields(createdSession.id, { subject, task })
          if (!result.ok) {
            setFormError(describeSaveFailure(result))
            return
          }
          setCreatedSession(result.session)
        } finally {
          setIsBusy(false)
        }
      }
      setActiveStep(from + 1)
      return
    }

    if (current.id === 'type') {
      if (!type) {
        setFormError('Pick a language before continuing.')
        return
      }
      setIsBusy(true)
      try {
        let row = createdSession
        if (!row) {
          row = await createSession()
          // Set immediately: if a follow-up write below fails, the next click must retry
          // that write, not create a second session.
          setCreatedSession(row)
          showToast('Session created. Your work is saved as you go.', 'success', 2500)
        }
        row = await seedTestCode(row)
        setCreatedSession(row)
        setRefreshToken((n) => n + 1)
        setActiveStep(from + 1)
      } catch (error) {
        setFormError(
          error.response?.data?.message ||
            error.message ||
            'VizPI could not create the session.'
        )
      } finally {
        setIsBusy(false)
      }
      return
    }

    // Steps that embed a self-persisting panel: read the row back FIRST so every decision
    // below is made against what was actually written, then advance.
    setIsBusy(true)
    try {
      // The Testcases step has no Create button of its own - Next compiles the harness.
      // This is the slow part of the whole wizard (a function-name call, a reference
      // solution, a Pyodide/Judge0 run, then one repair call per failing case), which is
      // why the footer button stays in its spinner for the duration.
      if (current.id === 'tests') {
        // Store the on/off answer (and the C++ main() template that follows from it)
        // before anything else, so every later step and the student view agree with it.
        let row = createdSession
        const synced = await syncTestsSetting(row)
        if (!synced.ok) {
          setFormError(
            describeSaveFailure(synced) +
              (testsOn ? '' : ' Or switch "Grade with test cases" back on to continue without saving it.')
          )
          return
        }
        try {
          row = await seedTestCode(synced.session || row)
        } catch (error) {
          setFormError(error.message)
          return
        }
        setCreatedSession(row)
      }

      if (current.id === 'tests' && testsOn) {
        const testApi = createUnitTestsRef.current
        if (!testApi) {
          setFormError('The test editor is still loading. Try again in a moment.')
          return
        }
        if (testApi.generating) {
          setFormError('Test cases are still being generated. Press Continue once they appear.')
          return
        }
        if (!testApi.testCount) {
          setFormError(
            'Add at least one test case before continuing — use Generate, or Add test case to write one by hand.'
          )
          return
        }
        const signature = JSON.stringify({
          // In saved order: students' results are matched to rows by position, so a list in a
          // different order than the harness needs a recompile. (A compile no longer
          // re-orders the list.) Public/hidden only changes what students see, not the
          // harness, so it is left out.
          // `suspect` is a class check's flag from the last compile (ClassCheckList), like
          // `newTest` for a case: not part of the harness.
          cases: (testApi.testList || []).map(({ newTest, suspect, hidden, ...testCase }) => JSON.stringify(testCase)),
          task: createdSession?.task,
          type: createdSession?.type,
          style: createdSession?.test_code?.test_style || 'function',
        })
        const unchanged = lastCompiledRef.current === signature && Boolean(createdSession?.test_code?.unit_test)
        if (!unchanged && lastCompiledRef.current && !recompileWarned) {
          setRecompileWarned(true)
          setFormError(
            'Your test cases changed, so Continue will regenerate the grading harness. That replaces Variable Setup and Unit Test, including any edits you made on Code Stubs. Press Continue again to go ahead.'
          )
          return
        }
        try {
          const outcome = unchanged ? null : await testApi.run()
          // Nothing was compiled (ClassCheckList: a check with no code yet).
          if (outcome?.blocked) {
            setFormError(outcome.blocked)
            return
          }
          if (!unchanged) {
            lastCompiledRef.current = signature
            setRecompileWarned(false)
          }
          if (outcome && outcome.flagged > 0 && !testsWarned) {
            setTestsWarned(true)
            // Read back the harness run() just wrote before pausing, so the next Next sees
            // it and does not mistake this for an uncompiled change.
            await refreshSession()
            setFormError(
              createdSession?.test_code?.test_style === 'class'
                ? `${outcome.flagged} of ${outcome.total} checks did not pass against the AI's reference class and are flagged above. Review them, then press Continue again.`
                : `${outcome.flagged} of ${outcome.total} cases disagree with the AI reference solution and are flagged above with a suggested replacement. Review them, then press Continue again.`
            )
            return
          }
        } catch (error) {
          console.error('Compiling the test harness failed:', error)
          setFormError(
            createdSession?.test_code?.test_style === 'class'
              ? 'Could not compile the checks. Check your connection, then try again.'
              : 'Could not compile the test harness. Check that the description names exactly one function, then try again.'
          )
          return
        }
      }

      // The last step always reads the row back too: the completeness check below has to
      // see what the earlier panels (TestList, GuiExerciseEditor, ...) actually wrote.
      let fresh = (current.persists || isLastStep ? await refreshSession() : null) || createdSession
      if (current.id === 'tests') {
        const ensured = await ensureTestCode(fresh)
        if (!ensured) {
          setFormError('Could not prepare the code stubs. Check your connection and press Continue again.')
          return
        }
        fresh = ensured
      }

      // The GUI and Pop Quiz panels persist only on their own explicit Save, so the
      // instructor can reach Finish with everything still in an unsaved edit buffer. Warn
      // once against the row we just read, while that editor is still on screen; a second
      // click goes through. In an experiment the Questions step is no longer last (the
      // Experiment step follows), so it is checked when leaving it instead.
      const leavingQuiz = experimentOn && current.id === 'quiz'
      if (isLastStep || leavingQuiz) {
        const warning = describeIncompleteness(fresh, isLastStep ? finishLabel : 'Continue')
        if (warning && !finishWarned) {
          setFinishWarned(true)
          setFormError(warning)
          return
        }
      }

      if (isLastStep) {
        if (experimentOn) {
          const problem = validateExperiment(experiment, fresh.type || type)
          if (problem) {
            setFormError(problem)
            return
          }
        }
        // Publish is the first call that needs a valid sign-in, possibly long after the
        // page loaded; refresh it so an expired token cannot lose the experiment settings.
        if (experimentOn) await refreshSignIn()
        // Experiment mode has no AI Chat step: the conditions carry their own settings.
        if (steps.some((step) => step.id === 'ai') && !(await saveAiSettings(fresh))) return
        if (experimentOn) {
          const result = await publishExperiment(
            buildPublishPayload(createdSession.id, experiment, fresh.type || type)
          )
          if (!result.ok) {
            // Already published (a double click, or a retry after a lost response): go to it.
            if (result.experimentId) {
              navigate(`/session/${result.experimentId}`)
              return
            }
            setFormError(result.message)
            return
          }
          showToast(`Experiment published with ${result.conditions.length} conditions.`, 'success', 3000)
          navigate(`/session/${result.experiment.id}`)
          return
        }
        navigate(`/session/${createdSession.id}`)
        return
      }
      setActiveStep(from + 1)
    } finally {
      setIsBusy(false)
    }
  }

  const handleBack = async () => {
    if (isBusy || conceptDrafting) return
    setFormError('')
    const from = activeStep
    if (from === 0) {
      navigate('/session')
      return
    }
    setIsBusy(true)
    try {
      if (current.persists) await refreshSession()
      setActiveStep(from - 1)
    } finally {
      setIsBusy(false)
    }
  }

  const handleRailClick = async (index) => {
    // Only allow jumping to a step already reached; forward movement stays gated by
    // handleNext's validation and the create call. Leaving a persisting step refetches,
    // exactly like Back does - otherwise the step jumped to renders against a row that
    // predates whatever the panel just saved.
    if (isBusy || conceptDrafting || index >= activeStep) return
    setFormError('')
    setIsBusy(true)
    try {
      if (current.persists) await refreshSession()
      setActiveStep(index)
    } finally {
      setIsBusy(false)
    }
  }

  // ---- live preview ----

  // In an experiment the preview shows one condition at a time ("Preview as", or the
  // condition card opened last).
  const previewIndex = experiment ? Math.min(previewConditionIndex, experiment.conditions.length - 1) : 0
  const previewCondition = experimentOn && experiment ? experiment.conditions[previewIndex] : null
  const previewPolicy = previewCondition ? conditionPolicy(previewCondition, type) || null : null

  // Before every preview chat message: make sure the server holds the policy as currently
  // drafted (in an experiment, the previewed condition's), so the preview answers the way
  // students will be answered.
  const ensurePolicySaved = useCallback(async () => {
    if (!createdSession?.id) return
    const desired = previewCondition ? previewPolicy : aiChatOn ? aiPolicy.trim() || null : null
    if (savedPolicyRef.current === desired) return
    const result = await saveAiChatPolicy(createdSession.id, desired)
    if (!result.ok) {
      // The server would answer under whatever policy it still holds, which is not what
      // is drafted here - so the preview message is not sent (the previews report it).
      throw new Error('Could not save the AI Chat Policy, so the preview message was not sent.')
    }
    savedPolicyRef.current = result.policy ?? desired
  }, [createdSession?.id, aiChatOn, aiPolicy, previewCondition, previewPolicy])

  // The session as students would get it right now: the saved row, plus everything still
  // only in this form (problem text, tests switch, and the open step's unsaved edits).
  const previewSession = useMemo(() => {
    if (!type) return null
    const row = createdSession || {}
    const kind = typeMeta?.kind
    const testCode = { ...(row.test_code || {}) }
    if (current?.id === 'stubs' && draftTemplate != null) testCode.template = draftTemplate
    return {
      ...row,
      type: row.type || type,
      subject,
      task,
      enable_tests: kind === 'code' ? testsOn : row.enable_tests,
      test_list: current?.id === 'tests' && draftTestList ? draftTestList : row.test_list || [],
      test_code: (row.test_code || current?.id === 'stubs') ? testCode : null,
    }
  }, [type, createdSession, typeMeta, current, draftTemplate, draftTestList, subject, task, testsOn])

  // A step's unsaved edits stop applying once it closes (the row is read back then); also
  // drop them so re-opening the step never flashes the previous visit's draft.
  useEffect(() => {
    setDraftTestList(null)
    setDraftTemplate(null)
  }, [current?.id])

  const previewTestsEnabled = typeMeta?.kind === 'code' ? testsOn : true
  const previewChatEnabled =
    Boolean(type) &&
    (previewCondition
      ? conditionAiChat(previewCondition, type)
      : aiChatSupport(type) !== 'unavailable' && aiChatOn)
  // Drop regions the current settings remove from the student view (no chat panel while
  // AI chat is off, no test list while test cases are off).
  const previewFocus = (PREVIEW_FOCUS[current?.id] || []).filter(
    (id) => (id !== 'chat' || previewChatEnabled) && (id !== 'tests' || previewTestsEnabled)
  )

  // ---- resizable split ----

  // Widest the steps column may get in the current window: the split's content width
  // minus the divider and the preview's minimum.
  const maxStepsWidth = () => {
    const layout = layoutRef.current
    if (!layout) return STEPS_WIDTH_MIN
    const style = window.getComputedStyle(layout)
    const content = layout.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
    return Math.max(STEPS_WIDTH_MIN, content - 12 - PREVIEW_WIDTH_MIN)
  }

  const startStepsResize = (event) => {
    if (!layoutRef.current) return
    event.preventDefault()
    const maxWidth = maxStepsWidth()
    // Follow the pointer by how far it moved, so the column does not jump on the first move.
    const startX = event.clientX
    const startWidth = Math.min(stepsWidth, maxWidth)
    let latest = startWidth
    const onMove = (e) => {
      latest = Math.min(maxWidth, Math.max(STEPS_WIDTH_MIN, startWidth + (e.clientX - startX)))
      setStepsWidth(latest)
    }
    const stop = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
      document.body.classList.remove('cq-resizing')
      dragCleanupRef.current = null
      writeStepsWidth(latest)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', stop)
    // A touch or pen drag that the browser takes over ends in pointercancel, not pointerup.
    window.addEventListener('pointercancel', stop)
    document.body.classList.add('cq-resizing')
    dragCleanupRef.current = stop
  }

  const nudgeStepsWidth = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const next = Math.min(
      maxStepsWidth(),
      Math.max(STEPS_WIDTH_MIN, stepsWidth + (event.key === 'ArrowLeft' ? -24 : 24))
    )
    setStepsWidth(next)
    writeStepsWidth(next)
  }

  // One-line recap shown on a finished step's collapsed header.
  const stepSummary = (stepId) => {
    switch (stepId) {
      case 'details':
        return subject
      case 'type':
        return typeMeta?.label || ''
      case 'tests': {
        if (!testsOn) return 'No test cases'
        const count = (createdSession?.test_list || []).length
        return `${count} test case${count === 1 ? '' : 's'}`
      }
      case 'stubs':
        return createdSession?.test_code?.template?.trim() ? 'Starter code set' : 'No starter code'
      case 'quiz': {
        const count = createdSession?.quiz?.questions?.length || 0
        return `${count} question${count === 1 ? '' : 's'}`
      }
      case 'ai':
        return aiChatOn ? `On${aiPolicy.trim() ? ' · policy set' : ''}` : 'Off'
      case 'experiment':
        return experiment
          ? `${experiment.conditions.length} conditions · ${assignmentLabel(experiment.assignment)}`
          : ''
      default:
        return ''
    }
  }

  const renderStep = () => {
    switch (current.id) {
      case 'details':
        return (
          <DetailsStep
            subject={subject}
            setSubject={setSubject}
            task={task}
            setTask={setTask}
            typeMeta={typeMeta}
          />
        )
      case 'type':
        return (
          <TypeStep type={type} setType={handleTypeChange} locked={Boolean(createdSession)} />
        )
      case 'tests':
        return (
          <TestCasesStep
            typeMeta={typeMeta}
            createUnitTestsRef={createUnitTestsRef}
            testsOn={testsOn}
            setTestsOn={setTestsOn}
            onTestListChange={setDraftTestList}
            busy={isBusy}
            onStyleChange={() => {
              // The tests and harness were cleared: nothing compiled is left to compare
              // against or to have warned about.
              lastCompiledRef.current = null
              setRecompileWarned(false)
              setTestsWarned(false)
              setFormError('')
            }}
          />
        )
      case 'stubs':
        return (
          <CodeStubStep
            typeMeta={typeMeta}
            refreshToken={refreshToken}
            onTemplateDraftChange={setDraftTemplate}
            onStarterSaved={refreshSession}
          />
        )
      case 'gui':
        return <GuiExerciseStep />
      case 'quiz':
        return <PopQuizStep />
      case 'ai':
        return (
          <AiChatStep
            type={createdSession?.type || type}
            aiChatOn={aiChatOn}
            setAiChatOn={setAiChatOn}
            aiPolicy={aiPolicy}
            setAiPolicy={setAiPolicy}
          />
        )
      case 'experiment':
        return (
          <ExperimentStep
            experiment={experiment}
            setExperiment={setExperiment}
            type={createdSession?.type || type}
            disabled={isBusy}
            drafting={conceptDrafting}
            setDrafting={setConceptDrafting}
            onSelectCondition={setPreviewConditionIndex}
          />
        )
      default:
        return null
    }
  }

  const stepHint = () => {
    if (!createdSession) return null
    if (current.id === 'tests' && testsOn) {
      return 'Continue compiles these cases into a runnable harness and checks them against an AI reference solution.'
    }
    if (current.id === 'ai') return 'These settings are saved when you click Finish.'
    if (current.id === 'experiment') return 'Nothing here is saved until you publish.'
    if (current.persists) return 'This step saves on its own — use its Save button or click out of a field.'
    return null
  }

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <div className="cq-wizard cq-wizard--split">
        <div className="cq-wizard__header">
          <div className="cq-wizard__title">
            <h1>{experimentOn ? 'Create Experiment' : HEADER_BY_KIND[typeMeta?.kind] || 'Create Problem'}</h1>
            {/* The row is created when the Language step is completed, not at Finish: the
                later steps' editors (tests, starter code, web exercise, quiz) save into it
                as you go. Say so in plain terms. */}
            <p>
              {experimentOn
                ? 'The exercise is saved as you go; the experiment settings are saved when you publish.'
                : createdSession
                ? 'Draft saved — you can finish it later from the session list.'
                : 'Saved as a draft once you continue past Language.'}
            </p>
          </div>
          <div className="cq-wizard__actions">
            {experimentSupported && (
              <Tooltip title="Make several versions (conditions) of this exercise and split students between them.">
                <FormControlLabel
                  className="cq-experiment-switch"
                  labelPlacement="start"
                  label="Experiment"
                  control={
                    <Switch
                      checked={experimentOn}
                      onChange={(e) => handleExperimentToggle(e.target.checked)}
                      disabled={isBusy}
                    />
                  }
                />
              </Tooltip>
            )}
            <Button onClick={handleLeave} disabled={isBusy}>
              {createdSession ? 'Leave' : 'Cancel'}
            </Button>
          </div>
        </div>

        <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)} aria-labelledby="cq-leave-title">
          <DialogTitle id="cq-leave-title">Leave without publishing?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {createdSession
                ? 'The exercise stays in your session list as a regular draft, but the experiment settings (conditions, assignment, concepts) are only saved when you publish.'
                : 'Nothing has been saved yet.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeaveOpen(false)} autoFocus>
              Stay
            </Button>
            <Button color="error" onClick={() => navigate('/session')}>
              Leave
            </Button>
          </DialogActions>
        </Dialog>

        <div className="cq-split" ref={layoutRef} style={{ '--cq-steps-width': `${stepsWidth}px` }}>
          {/* ---- steps: one accordion section per step ---- */}
          <ol className="cq-steps" aria-label="Steps">
            {steps.map((step, index) => {
              const isActive = index === activeStep
              const isDone = index < activeStep
              const isLocked = index > activeStep
              const summary = isDone ? stepSummary(step.id) : ''
              return (
                <li
                  key={step.id}
                  className={[
                    'cq-step',
                    isActive ? 'is-active' : '',
                    isDone ? 'is-done' : '',
                    isLocked ? 'is-locked' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}>
                  <button
                    type="button"
                    className="cq-step__header"
                    aria-expanded={isActive}
                    aria-current={isActive ? 'step' : undefined}
                    disabled={isLocked || isActive || isBusy}
                    onClick={() => handleRailClick(index)}>
                    <span className="cq-step__bullet">
                      {isDone ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                    </span>
                    <span className="cq-step__titles">
                      <span className="cq-step__label">{step.label}</span>
                      {summary && <span className="cq-step__summary">{summary}</span>}
                    </span>
                    {isLocked && <LockOutlinedIcon className="cq-step__icon" fontSize="small" />}
                    {isDone && <ExpandMoreIcon className="cq-step__icon" fontSize="small" />}
                  </button>

                  {isActive && (
                    <div
                      className="cq-step__body"
                      ref={activeBodyRef}
                      tabIndex={-1}
                      aria-label={step.label}>
                      {renderStep()}
                      {formError && (
                        <Alert severity="error" sx={{ mt: 2 }} ref={errorRef}>
                          {formError}
                        </Alert>
                      )}
                      <div className="cq-step__actions">
                        {activeStep > 0 && (
                          <Button onClick={handleBack} disabled={isBusy || conceptDrafting}>
                            Back
                          </Button>
                        )}
                        <span className="cq-saved-hint">{stepHint()}</span>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleNext}
                          disabled={isBusy || conceptDrafting}>
                          {isBusy ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : isLastStep ? (
                            finishLabel
                          ) : (
                            'Continue'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              )
            })}
          </ol>

          <div
            className="cq-split__divider"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the steps and preview columns"
            aria-valuenow={stepsWidth}
            tabIndex={0}
            onPointerDown={startStepsResize}
            onKeyDown={nudgeStepsWidth}
          />

          {/* ---- live student preview ---- */}
          <div className="cq-live">
            <LiveStudentPreview
              session={previewSession}
              testsEnabled={previewTestsEnabled}
              chatEnabled={previewChatEnabled}
              highlight={previewFocus}
              resetToken={previewResetToken}
              onReset={() => setPreviewResetToken((n) => n + 1)}
              onBeforeChat={ensurePolicySaved}
              headerExtra={
                previewCondition && (
                  <TextField
                    select
                    size="small"
                    label="Preview as"
                    value={previewIndex}
                    onChange={(e) => setPreviewConditionIndex(Number(e.target.value))}
                    className="lp-panel__condition">
                    {experiment.conditions.map((condition, index) => (
                      <MenuItem key={condition.rowId} value={index}>
                        {condition.name.trim() || `Condition ${index + 1}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )
              }
            />
          </div>
        </div>
      </div>
    </SessionContext.Provider>
  )
}

export default CreateQuestionWizard
